import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sunrise, Sun, SunMedium, Sunset, Moon, MoonStar, CloudRain, CloudLightning, CloudSnow, Cloud } from "lucide-react";
import { useCurrent } from "@/hooks/useWeather";
import { CONDITION_LABEL, CONDITION_ICON } from "@/lib/weatherUtils";

const PHASES = [
  { key: "alba",       label: "Alba",       from: 5,  to: 8,  Icon: Sunrise,   tint: "#FDBA74" },
  { key: "mattina",    label: "Mattina",    from: 8,  to: 12, Icon: Sun,       tint: "#FBBF24" },
  { key: "pomeriggio", label: "Pomeriggio", from: 12, to: 17, Icon: SunMedium, tint: "#F59E0B" },
  { key: "tramonto",   label: "Tramonto",   from: 17, to: 20, Icon: Sunset,    tint: "#F97316" },
  { key: "sera",       label: "Sera",       from: 20, to: 22, Icon: Moon,      tint: "#CBD5E1" },
  { key: "notte",      label: "Notte",      from: 22, to: 29, Icon: MoonStar,  tint: "#E5E7EB" },
];

function currentPhaseIndex(hour) {
  if (hour >= 22 || hour < 5) return PHASES.length - 1;
  return PHASES.findIndex((p) => hour >= p.from && hour < p.to);
}

export default function DayPhaseTimeline() {
  const { data } = useCurrent();
  const [hour, setHour] = useState(() => new Date().getHours());

  useEffect(() => {
    const t = setInterval(() => setHour(new Date().getHours()), 60_000);
    const onVis = () => setHour(new Date().getHours());
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const phases = useMemo(() => {
    const activeIdx = currentPhaseIndex(hour);
    const cond = data?.condition;
    if (cond === "rain" || cond === "storm" || cond === "snow") {
      const wIcon = cond === "storm" ? CloudLightning : cond === "snow" ? CloudSnow : CloudRain;
      const wTint = cond === "storm" ? "#FBBF24" : cond === "snow" ? "#E0F2FE" : "#38BDF8";
      return PHASES.map((p, i) => i === activeIdx ? { ...p, Icon: wIcon, tint: wTint } : p);
    }
    return PHASES;
  }, [data?.condition, hour]);

  const activeIdx = currentPhaseIndex(hour);
  const active = phases[activeIdx];
  const cond = data?.condition || "clear";
  const CondIcon = CONDITION_ICON[cond] || Cloud;
  const condLabel = CONDITION_LABEL[cond] || "—";
  const temp = data?.temperature;

  return (
    <div className="flex items-center gap-3" data-testid="day-phase-timeline">
      {/* Compact rail */}
      <div className="flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur border border-slate-200/70 px-3 py-1.5 shadow-sm">
        {phases.map((p, i) => {
          const isActive = i === activeIdx;
          const Icon = p.Icon;
          return (
            <div
              key={p.key}
              className="relative flex items-center"
              data-testid={`phase-${p.key}${isActive ? "-active" : ""}`}
            >
              <motion.div
                initial={false}
                animate={{ scale: isActive ? 1 : 0.85, opacity: isActive ? 1 : 0.5 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className={`h-7 w-7 rounded-full flex items-center justify-center transition-all ${isActive ? "shadow-md" : ""}`}
                style={{ background: isActive ? p.tint : "transparent", color: isActive ? "#0B132B" : "#64748B" }}
                aria-label={p.label}
                title={p.label}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={2.2} />
              </motion.div>
              {i < phases.length - 1 && <span className="mx-0.5 h-px w-3 bg-slate-300/60" aria-hidden />}
            </div>
          );
        })}
      </div>

      {/* Meta: fase + condizione meteo attuale */}
      <div className="hidden xl:flex items-center gap-3 pl-1" data-testid="phase-meta">
        <div className="flex flex-col leading-tight">
          <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Fase</span>
          <span className="text-sm font-medium text-[#0B132B]">{active.label}</span>
        </div>
        <span className="h-8 w-px bg-slate-200" />
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-full bg-[#0B132B] text-[#FBBF24] flex items-center justify-center">
            <CondIcon className="h-4 w-4" />
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500">Ora</span>
            <span className="text-sm font-medium text-[#0B132B]" data-testid="current-condition">
              {condLabel}{typeof temp === "number" ? ` · ${Math.round(temp)}°` : ""}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
